import { useEffect, useState, useMemo } from "react";
import CanvasJSReact from "@canvasjs/react-charts";
import apiLink from "../../api/apiLink";

const CanvasJSChart = CanvasJSReact.CanvasJSChart;

const LiveBandwidth = ({ customer }) => {
  const [chunkData, setChunkData] = useState([]);
  const [dps1, setDps1] = useState([]);
  const [dps2, setDps2] = useState([]);
  const [xVal, setXVal] = useState(0); // X-axis values
  const [count, setCount] = useState(0);

  const dataLength = 70; // Number of visible data points

  // Helper function to format data to Mbps or Kbps
  const formatToMbps = (value) => {
    return (value / 1024).toFixed(2); // Convert to Kbps otherwise
  };

  useEffect(() => {
    setCount((prev) => {
      if (prev + 1 < 51) {
        fetchBandwidthData();
      }
      return prev + 1;
    });
  }, [customer?.id]);

  const fetchBandwidthData = async () => {
    if (count >= 51) return;

    try {
      const response = await apiLink.get(
        `customer/mikrotik/bandwidth?customerId=${customer?.id}`
      );

      if (response.status === 200) {
        setChunkData(response.data?.data || []);

        setCount((prev) => {
          if (prev + 1 < 71) {
            fetchBandwidthData();
          }
          return prev + 1;
        });
      }
    } catch (error) {
      console.log(error.message);
    }
  };

  useEffect(() => {
    if (chunkData.length > 0) {
      const rxValue = chunkData[0]?.rx || 0;
      const txValue = chunkData[1]?.tx || 0;

      // Update data points for both download and upload
      setDps1((prevDps1) => {
        const newDps1 = [
          ...prevDps1,
          { x: xVal, y: Number(formatToMbps(rxValue)) },
        ];
        return newDps1.length > dataLength ? newDps1.slice(1) : newDps1;
      });

      setDps2((prevDps2) => {
        const newDps2 = [
          ...prevDps2,
          { x: xVal, y: Number(formatToMbps(txValue)) },
        ];
        return newDps2.length > dataLength ? newDps2.slice(1) : newDps2;
      });

      setXVal((prevXVal) => prevXVal + 1); // Increment xVal for the next update
    }
  }, [chunkData]);

  // Memoize chart options for performance optimization
  const options = useMemo(
    () => ({
      height: window.innerHeight * 0.3,
      axisY: {
        labelFormatter: () => " ", // Hide labels
        tickLength: 0, // Remove ticks
      },
      axisX: {
        minimum: xVal - dataLength + 1, // Dynamic x-axis start point
        maximum: xVal, // Current xVal as end point
      },
      legend: {
        verticalAlign: "top", // Move legend to the top
        horizontalAlign: "center", // Center align the legend
        fontSize: 14, // Optional: Change font size
        fontWeight: "bold", // Optional: Make text bold
      },
      data: [
        {
          type: "column",
          showInLegend: true,
          legendText: `Upload: ${
            dps1[dps1.length - 1]?.y / 1024 >= 1
              ? (dps1[dps1.length - 1]?.y / 1024).toFixed(2)
              : dps1[dps1.length - 1]?.y
          } ${Number(dps1[dps1.length - 1]?.y / 1024) >= 1 ? "Mbps" : "Kbps"}`,
          dataPointWidth: 2,
          dataPoints: dps1,
        },
        {
          type: "column",
          showInLegend: true,
          legendText: `Download: ${
            dps2[dps2.length - 1]?.y / 1024 >= 1
              ? (dps2[dps2.length - 1]?.y / 1024).toFixed(2)
              : dps2[dps2.length - 1]?.y
          } ${Number(dps2[dps2.length - 1]?.y / 1024) >= 1 ? "Mbps" : "Kbps"}`,
          dataPointWidth: 2,
          dataPoints: dps2,
        },
      ],
    }),
    [xVal, dps1, dps2]
  );

  return (
    <div>
      <CanvasJSChart options={options} />
    </div>
  );
};

export default LiveBandwidth;
